import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../../utils/httpRequest";
import {
  EnrolmentCertificate,
  OngoingCourse,
} from "../../../stores/EnrolmentStore/Types";
import FeaturedCourses from "../../CoursePreview/components/FeaturedCourses";
import Confetti from "../../common/Confetti";
import CertificateStage from "../../common/CertificateStage";
import CertificateFacts from "../../common/CertificateFacts";
import { notification } from "../../../utils/helpers";
import NewReviewModal from "../NewReviewModal";
import { CurrentUserResource } from "../../../stores/CurrentUserStore/Types";
import {
  Sparkle,
  DownloadIcon,
  LinkIcon,
  LandscapeIcon,
  PortraitIcon,
  MailIcon,
  XIcon,
  LinkedInIcon,
  FacebookIcon,
} from "./Icons";

interface Props {
  enrolmentId: any;
  enrolment: OngoingCourse;
  currentUser?: CurrentUserResource;
}

interface State {
  certificate: EnrolmentCertificate;
  showReviewModal: boolean;
  loading: boolean;
  orientation: "landscape" | "portrait";
}

const brandIdMapping = {
  "cannes-lions": 436863,
  parsons: 301086,
  ogilvy: 1673,
  "happiness-research-institute": 2947845,
};

const FOURTYTWO_COURSES_BRAND_ID = 10274769;

// Weighted to the deeper golds so the pieces stay visible against the
// near-white page behind them.
const CONFETTI_COLOURS = [
  "#F5B041",
  "#E9A23B",
  "#F3D24E",
  "#EAB73C",
  "#C4841D",
  "#F7C948",
];

// Portrait fills a phone screen far better; landscape suits a desktop. The
// learner can still toggle either way.
const PORTRAIT_BREAKPOINT = "(max-width: 760px)";

class Certificate extends Component<Props, State> {
  constructor(props) {
    super(props);

    const prefersPortrait =
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia(PORTRAIT_BREAKPOINT).matches;

    this.state = {
      certificate: null,
      loading: true,
      showReviewModal: false,
      orientation: prefersPortrait ? "portrait" : "landscape",
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchCertificate();
  }

  fetchCertificate = () => {
    const { enrolmentId } = this.props;

    return getRequest(`/api/enrolments/${enrolmentId}/certificate.json`)
      .then((response: AxiosResponse) => {
        this.setState({ certificate: response.data, loading: false });
      })
      .catch((error) => {
        this.setState({ loading: false });
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  toggleNewReviewModal = (e?: any) => {
    e?.preventDefault();
    this.setState({ showReviewModal: !this.state.showReviewModal });
  };

  setOrientation = (orientation: "landscape" | "portrait") => {
    this.setState({ orientation });
  };

  publicUrl = () => this.state.certificate?.share_urls?.platform || "";

  copyUrlToClipboard = (e) => {
    e.preventDefault();

    const url = this.publicUrl();
    if (!url) return;

    const done = () => notification("Verification link copied", "alert");

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done, () => this.fallbackCopy(url, done));
    } else {
      this.fallbackCopy(url, done);
    }
  };

  fallbackCopy = (text: string, done: () => void) => {
    const field = document.createElement("textarea");
    field.value = text;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();

    try {
      document.execCommand("copy");
      done();
    } catch (e) {
      notification("Copy failed, long-press to copy", "alert");
    }

    document.body.removeChild(field);
  };

  getBrandId = () =>
    brandIdMapping[this.state.certificate?.brand_slug] || FOURTYTWO_COURSES_BRAND_ID;

  // LinkedIn's add-to-profile deep link. Passing certId alongside certUrl is
  // what makes the entry verifiable rather than a bare line of text.
  linkedInAddToProfileUrl = () => {
    const { certificate } = this.state;

    const params = {
      startTask: "CERTIFICATION_NAME",
      name: certificate?.course_name,
      organizationId: this.getBrandId(),
      issueYear: certificate?.enrolment?.completed_at_year,
      issueMonth: certificate?.enrolment?.completed_at_month,
      certUrl: this.publicUrl(),
      certId: certificate?.credential_id,
    };

    const query = Object.keys(params)
      .filter((key) => params[key] !== null && params[key] !== undefined)
      .map((key) => `${key}=${encodeURIComponent(params[key])}`)
      .join("&");

    return `https://www.linkedin.com/profile/add?${query}`;
  };

  openShareWindow = (e) => {
    e.preventDefault();
    window.open(
      e.currentTarget.href,
      "share",
      "noopener,noreferrer,width=620,height=640"
    );
  };

  // Two variants only: below the 75% pass mark there is no certificate, so
  // there is no third line to write.
  lede = () => {
    const score = this.props.enrolment?.score;

    return score === 100
      ? "A perfect score, and top of the class. Genuinely hard to pull off."
      : "You cleared a deliberately tough bar. The certificate is yours.";
  };

  render() {
    const { enrolment, enrolmentId, currentUser } = this.props;
    const { loading, certificate, showReviewModal, orientation } = this.state;
    const { course } = enrolment || {};

    if (loading) {
      return (
        <div className="ui active inverted dimmer">
          <div className="ui text loader">Loading</div>
        </div>
      );
    }

    // This page is behind login, but the resource is a union so the greeting
    // has to survive the absent case rather than assume it away.
    const firstName =
      currentUser?.kind === "current-user" ? currentUser.first_name : null;
    const courseName = certificate?.course_name || course?.name;
    const downloadUrl = `/enrolments/${enrolmentId}/certificate/download`;
    const publicUrl = this.publicUrl();
    const shareText = `I just passed ${courseName} on 42courses.`;
    const cpdHours = certificate?.cpd_hours;

    return (
      <div className="cert-page">
        {/* The confetti sits over the headline AND the certificate rather than
            inside the headline section, which is only ~300px tall and clipped
            -- confined there it petered out a third of the way down the page.
            Quick and plentiful, too: this is the moment someone passed, so it
            should read as a party popper, not ticker tape at a state occasion.
            The public page keeps the statelier default; it is a document. */}
        <div className="cert-top">
          <Confetti
            colours={CONFETTI_COLOURS}
            travel={[0.55, 0.9]}
            duration={[1.4, 2.4]}
            pieces={80}
          />
          <section className="cert-celebrate" aria-labelledby="cert-congrats">
            <div className="cert-celebrate__inner">
              <span className="cert-eyebrow">
                <Sparkle />
                Certificate earned
              </span>
              <h1 id="cert-congrats">
                {firstName ? `Well played, ${firstName}.` : "Well played."}
              </h1>
              <p className="cert-celebrate__course">
                You passed <em>{courseName}</em>
              </p>
              <p className="cert-celebrate__lede">{this.lede()}</p>
            </div>
          </section>

          <section className="cert-stage-wrap" aria-labelledby="cert-stage-h">
          <h2 id="cert-stage-h" className="sr-only">
            Your certificate
          </h2>

          <div
            className="cert-orient"
            role="group"
            aria-label="Choose certificate orientation"
          >
            <button
              type="button"
              aria-pressed={orientation === "landscape"}
              onClick={() => this.setOrientation("landscape")}
            >
              <LandscapeIcon /> Landscape
            </button>
            <button
              type="button"
              aria-pressed={orientation === "portrait"}
              onClick={() => this.setOrientation("portrait")}
            >
              <PortraitIcon /> Portrait
            </button>
          </div>

          <CertificateStage
            orientation={orientation}
            landscapeUrl={certificate?.landscape_url}
            portraitUrl={certificate?.portrait_url}
            alt={`${courseName} certificate awarded to ${certificate?.user_name}`}
            href={downloadUrl}
          />

          <div className="cert-actions">
            {/* Downloads both orientations as a zip, which is what the
                existing endpoint serves. */}
            <a className="btn btn-blue btn--lg" href={downloadUrl} target="_blank" rel="noopener noreferrer">
              <DownloadIcon /> Download certificate
            </a>
            <a
              className="btn btn--lg btn-linkedin"
              href={this.linkedInAddToProfileUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInIcon /> Add to LinkedIn
            </a>
          </div>
          </section>
        </div>

        {publicUrl && (
          <section className="cert-share" aria-labelledby="cert-share-h">
            <h2 id="cert-share-h" className="cert-share__label">
              Or share it
            </h2>
            <div className="cert-share__row">
              <button
                className="cert-share__btn"
                type="button"
                aria-label="Copy verification link"
                onClick={this.copyUrlToClipboard}
              >
                <LinkIcon />
              </button>
              <a
                className="cert-share__btn"
                aria-label="Share on X"
                href={certificate?.share_urls?.twitter}
                onClick={this.openShareWindow}
              >
                <XIcon />
              </a>
              <a
                className="cert-share__btn"
                aria-label="Share on Facebook"
                href={certificate?.share_urls?.facebook}
                onClick={this.openShareWindow}
              >
                <FacebookIcon />
              </a>
              <a
                className="cert-share__btn"
                aria-label="Share by email"
                href={`mailto:?subject=${encodeURIComponent(
                  shareText
                )}&body=${encodeURIComponent(
                  `${shareText} Have a look: ${publicUrl}`
                )}`}
              >
                <MailIcon />
              </a>
            </div>
          </section>
        )}

        {/* The same card the public verification page shows, so a learner sees
            exactly what a recruiter will. "Issued by" is dropped here -- the
            header already says 42courses issued it, and on your own page that
            leaves room for the XP, which is the one fact that is yours rather
            than the certificate's. */}
        <section className="cert-results" aria-labelledby="cert-results-h">
          <h2 id="cert-results-h" className="sr-only">
            Certificate details
          </h2>
          <CertificateFacts
            facts={[
              {
                label: "Result",
                value: enrolment?.score ? `Passed, ${enrolment.score}%` : "Passed",
              },
              { label: "CPD", value: cpdHours ? `${cpdHours} hours` : "" },
              {
                label: "XP gained",
                value: enrolment?.xp ? `+${enrolment.xp.toLocaleString()}` : "",
              },
              {
                label: "Credential ID",
                value: certificate?.credential_id,
                mono: true,
              },
            ]}
          />
        </section>

        {!certificate?.reviewed && (
          <section className="cert-nudge" aria-labelledby="cert-nudge-h">
            <div className="cert-nudge__inner">
              <div className="cert-nudge__txt">
                <h2 id="cert-nudge-h">Please Help!</h2>
                <p>
                  We spent an unreasonable amount of time on this course and we
                  would rather like to know if it landed. Praise, gripes,
                  half-formed thoughts: all welcome :)
                </p>
              </div>
              <button
                className="btn btn-white"
                type="button"
                onClick={this.toggleNewReviewModal}
              >
                Leave a review
              </button>
            </div>
          </section>
        )}

        {/* FeaturedCourses renders .related-grid and .course-card, and neither
            is styled on its own: .course-card lives under .facelift-landing-page
            and .related-grid under .facelift-course-preview. The course preview
            page carries both classes together for exactly this reason, so this
            rail does the same. Drop either and the tiles fall back to unstyled
            stacked blocks. */}
        {course?.slug && (
          <div className="cert-next facelift-landing-page facelift-course-preview">
            <FeaturedCourses
              courseSlug={course.slug}
              sectionTitle="Where to go from here"
              userIsInUk={currentUser?.is_in_uk}
              availableOnly
            />
          </div>
        )}

        <NewReviewModal
          enrolmentId={enrolmentId}
          courseName={courseName}
          showModal={showReviewModal}
          handleHideNewReviewModal={this.toggleNewReviewModal}
          refreshCertificate={this.fetchCertificate}
        />
      </div>
    );
  }
}

export default Certificate;
